import {Component, OnInit} from '@angular/core';
import {environment} from '../../../environments/environment';
import {ApiService} from '../../shared/services/api.service';
import {HttpClient} from '@angular/common/http';
import {faMagicWandSparkles, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {DevToolAccount, DevToolConfig} from '../../shared/models/devtool';
import {QueueService} from '../../shared/services/queue.service';
import {StorageService} from '../../shared/services/storage.service';
declare const window: any;

@Component({
  selector: 'app-devtool',
  templateUrl: './devtool.component.html',
  styleUrls: ['./devtool.component.scss']
})
export class DevtoolComponent implements OnInit {
  showDevTool = !environment.production;
  showMenu = false;
  showAccounts = true;
  showPassports = false;
  config: DevToolConfig = {
    fakePassports: [],
    accounts: []
  };
  isDragging = false;
  position = {x: 20, y: 60};
  dragStartPosition = {x: 0, y: 0};

  constructor(public api: ApiService,
    private loader: LoaderService,
    private queue: QueueService,
    private lang: LangService,
    private nav: NavigateService,
    private storage: StorageService,
    private http: HttpClient) {}

  ngOnInit() {
    this.loadConfig();
  }

  private loadConfig() {
    if(localStorage.getItem('devToolConfig')) {
      this.config = JSON.parse(localStorage.getItem('devToolConfig'));
    }
    this.http.get('assets/devtool-config.json').subscribe({
      next: (config: DevToolConfig) => {
        this.config = config;
        localStorage.setItem('devToolConfig', JSON.stringify(config));
      },
      error: (error) => {
        console.error('Error reading devtool config:', error);
      }
    });
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
    if(!this.showMenu) {
      this.showAccounts = true;
      this.showPassports = false;
    }
  }

  showAccountList() {
    this.showAccounts = !this.showAccounts;
    this.showPassports = false;
  }

  showPassportList() {
    this.showPassports = !this.showPassports;
    this.showAccounts = false;
  }

  selectAccount(account: DevToolAccount) {
    this.api.signOut().finally(() => {
      this.nav.to('sign-in', undefined, {
        queryParams: {
          email: account.username,
          password: account.password,
          place: account.place
        }
      });
    });
  }

  togglePassport(passportName?: string) {
    if(!passportName) {
      this.api.devtoolFakePassport = undefined;
    } else {
      this.api.devtoolFakePassport = JSON.parse(JSON.stringify(this.config.fakePassports.find(p => p.name === passportName)));
      if(!this.api.devtoolFakePassport.data.documentNumber || !this.api.devtoolFakePassport.data.fields.documentNumber) {
        // Generate a random document number such as BE123456 using the first two letters of the administrativeCode
        const nationality = this.api.devtoolFakePassport.data.fields.nationality;
        const randomDocumentNumber = nationality.substring(0, 2).toUpperCase() + Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
        this.api.devtoolFakePassport.data.documentNumber = randomDocumentNumber;
        this.api.devtoolFakePassport.data.fields.documentNumber = randomDocumentNumber;
      }
    }
  }

  windowReload() {
    window.location.reload();
  }

  clearStorage() {
    this.loader.loading(true, {type: 'error', btnLabel: 'global.yes', custom: {icon: 'trash', innerHtml: this.lang.transform('ask.clear.storage')}}).then((done) => {
      if(done) {
        this.loader.loading(true);
        
        // Clear all storage types
        this.clearAllStorage();
        
        // Clear service workers
        this.clearServiceWorkers();
        
        // Clear cache storage
        this.clearCacheStorage();
        
        // Clear cookies (if possible)
        this.clearCookies();
        
        // Clear IndexedDB databases
        this.clearIndexedDB();
        
        // Clear WebSQL (if available)
        this.clearWebSQL();
        
        // Clear application cache (if available)
        this.clearApplicationCache();
        
        setTimeout(() => {
          this.loader.loading(false);
          this.windowReload();
        }, 2000);
      }
    });
  }

  private async clearAllStorage() {
    try {
      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Export queue before clearing
      this.queue.exportQueueToJSON(false);
      this.queue.stop();
      
      // Close existing IndexedDB connections
      this.storage.idbstorage?.close();
    } catch (error) {
      console.error('Error clearing basic storage:', error);
    }
  }

  private async clearServiceWorkers() {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
    } catch (error) {
      console.error('Error clearing service workers:', error);
    }
  }

  private async clearCacheStorage() {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
    } catch (error) {
      console.error('Error clearing cache storage:', error);
    }
  }

  private clearCookies() {
    try {
      // Clear cookies for current domain
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos) : c;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
      });
    } catch (error) {
      console.error('Error clearing cookies:', error);
    }
  }

  private async clearIndexedDB() {
    try {
      if ('indexedDB' in window) {
        // Get all databases and delete them
        const databases = await this.getAllIndexedDBDatabases();
        for (const dbName of databases) {
          try {
            await new Promise<void>((resolve, reject) => {
              const deleteReq = indexedDB.deleteDatabase(dbName);
              deleteReq.onsuccess = () => resolve();
              deleteReq.onerror = () => reject(deleteReq.error);
              deleteReq.onblocked = () => {
                console.warn(`Database ${dbName} is blocked, retrying...`);
                setTimeout(() => resolve(), 100);
              };
            });
          } catch (error) {
            console.error(`Error deleting database ${dbName}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error clearing IndexedDB:', error);
    }
  }

  private async getAllIndexedDBDatabases(): Promise<string[]> {
    // This is a workaround since there's no direct API to list all databases
    // We'll try to delete common database names and the known one
    const commonDbNames = ['lu.satoris.paycode'];
    
    // Add any other known database names from your application
    return commonDbNames;
  }

  private clearWebSQL() {
    try {
      // WebSQL is deprecated but still exists in some browsers
      if ('openDatabase' in window) {
        // WebSQL doesn't have a direct way to list databases
        // This is a best-effort approach
        console.log('WebSQL detected but cannot be cleared programmatically');
      }
    } catch (error) {
      console.error('Error clearing WebSQL:', error);
    }
  }

  private clearApplicationCache() {
    try {
      // Application Cache is deprecated but still exists in some browsers
      if ('applicationCache' in window) {
        window.applicationCache.update();
        // Note: There's no direct way to clear application cache programmatically
        console.log('Application Cache detected but cannot be cleared programmatically');
      }
    } catch (error) {
      console.error('Error clearing application cache:', error);
    }
  }

  onMouseDown(event: MouseEvent) {
    this.isDragging = true;
    this.dragStartPosition = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y
    };
    event.preventDefault();
  }

  onMouseMove(event: MouseEvent) {
    if(this.isDragging) {
      this.position = {
        x: event.clientX - this.dragStartPosition.x,
        y: event.clientY - this.dragStartPosition.y
      };
    }
  }

  onMouseUp() {
    this.isDragging = false;
  }

  openInspector() {
    if(this.api.isElectronApp && window.electron) {
      window.electron.dev();
      return true;
    }
    return false;
  }

  protected readonly faMagicWandSparkles = faMagicWandSparkles;
}
